import { Controller } from "@hotwired/stimulus"
import { track } from "../lib/mixpanel"

// Pre-construction step 1 — the Plans and specs card (also on top of the By
// cost code view) and the owner's bid form card: uploads straight to S3
// (presign → PUT → register; each file is a chip with a spinner while it
// goes), remove a file, Watch AI read them again, and "Build the budget
// from" (the plans/specs or the owner's template; the cards swap at once,
// the server keeps the choice). Server calls go through the page's precon
// controller, which swaps in the panels it answers with.
export default class extends Controller {
  static targets = ["files", "chips", "input", "sub"]
  static values = { mode: String }

  connect() {
    try { this._strings = JSON.parse(this.element.querySelector("script[data-plans-strings]")?.textContent || "{}") } catch (e) { this._strings = {} }
  }

  get page() {
    const el = this.element.closest("[data-controller~='precon']")
    return el ? this.application.getControllerForElementAndIdentifier(el, "precon") : null
  }

  _t(key, vars = {}) {
    let s = key.split(".").reduce((o, k) => (o && o[k] !== undefined ? o[k] : undefined), this._strings || {})
    if (s && typeof s === "object") s = vars.count === 1 ? s.one : s.other
    if (typeof s !== "string") return key
    return s.replace(/%\{(\w+)\}/g, (_, k) => (vars[k] !== undefined ? vars[k] : ""))
  }

  async _send(method, path, body, ok) {
    const page = this.page
    if (!page) return null
    try {
      const q = page._viewQuery
      const data = await page._json(method, `${page.baseUrlValue}/${path}${q ? (path.includes("?") ? "&" : "?") + q : ""}`, body)
      page._applyResponse(data)
      if (ok) page._showToast(ok)
      return data
    } catch (e) {
      page._showToast(e.message, "error")
      return null
    }
  }

  // ── Build the budget from ────────────────────────────────────────────
  setMode(e) {
    const mode = e.currentTarget.dataset.mode
    if (mode === this.modeValue) return
    this.modeValue = mode
    this.element.querySelectorAll(".bmode .segbtn").forEach(b => b.classList.toggle("on", b.dataset.mode === mode))
    if (this.hasSubTarget) this.subTarget.textContent = mode === "owner" ? this.subTarget.dataset.owner : this.subTarget.dataset.plans
    const root = this.element.closest("[data-controller~='precon-s1']")
    if (root) {
      root.querySelector("[data-s1-card='csi']")?.classList.toggle("hidden", mode === "owner")
      root.querySelector("[data-s1-card='owner']")?.classList.toggle("hidden", mode !== "owner")
    }
    track("Precon Budget Source Switched", { project_id: this.page?.projectIdValue, mode })
    this._send("PATCH", "cost_codes/settings", { budget_mode: mode })
  }

  // ── AI reads the plans ───────────────────────────────────────────────
  readPlans(e) {
    if (e?.currentTarget) e.currentTarget.disabled = true
    track("Precon Plans Read Clicked", { project_id: this.page?.projectIdValue })
    this._send("POST", "documents/read_plans", {}, this._t("plans.read_started"))
  }

  // ── Files ─────────────────────────────────────────────────────────────
  removeDocument(e) {
    e.preventDefault()
    e.stopPropagation()
    const id = e.currentTarget.dataset.documentId
    const chip = e.currentTarget.closest(".chip")
    this.page?._confirm(this._t("plans.remove_title"), this._t("plans.remove_text"), this._t("plans.remove"), async () => {
      if (chip) chip.classList.add("hidden")
      const data = await this._send("DELETE", `documents/${id}`, undefined, this._t("plans.removed"))
      if (!data && chip) chip.classList.remove("hidden")
    })
  }

  // "Replace the file" on the owner's bid form: it comes off the job and the
  // drop zone is back.
  replaceOwnerForm(e) {
    const id = e.currentTarget.dataset.documentId
    this._send("DELETE", `documents/${id}`, undefined, this._t("own.replaced"))
  }

  dragOver(e) {
    e.preventDefault()
    e.currentTarget.classList.add("s1over")
  }

  dragLeave(e) { e.currentTarget.classList.remove("s1over") }

  drop(e) {
    e.preventDefault()
    e.stopPropagation()
    e.currentTarget.classList.remove("s1over")
    this._upload(Array.from(e.dataTransfer?.files || []), e.currentTarget.dataset.kind)
  }

  pick(e) {
    const kind = e.currentTarget.dataset.kind
    const input = this.inputTargets.find(i => i.dataset.kind === kind) || this.inputTargets[0]
    if (input) input.click()
  }

  picked(e) {
    this._upload(Array.from(e.target.files || []), e.target.dataset.kind)
    e.target.value = ""
  }

  async _upload(files, kind) {
    const page = this.page
    if (!page || !files.length) return
    if (kind === "owner_form") {
      files = files.slice(0, 1)
      if (!/\.(xlsx|xls|csv|pdf)$/i.test(files[0].name)) { page._showToast(this._t("own.bad_file"), "error"); return }
    }
    const box = this.hasChipsTarget ? this.chipsTarget : null
    if (box) box.classList.remove("hidden")
    const chips = files.map(f => this._chip(box, f.name))
    try {
      const presign = await page._json("POST", page.presignUrlValue, { files: files.map(f => ({ filename: f.name, content_type: f.type || "application/octet-stream", byte_size: f.size })) })
      await Promise.all(presign.uploads.map((u, i) => fetch(u.upload_url, { method: "PUT", body: files[i], headers: { "Content-Type": u.content_type || files[i].type || "application/octet-stream" } })
        .then(r => { if (!r.ok) throw new Error(this._t("plans.upload_failed", { name: files[i].name })) })
        .then(() => this._chipDone(chips[i]))))
      const q = page._viewQuery
      const data = await page._json("POST", `${page.registerUrlValue}${q ? "?" + q : ""}`, { kind, files: presign.uploads.map((u, i) => ({ cache_name: u.cache_name, filename: files[i].name, content_type: files[i].type, byte_size: files[i].size })) })
      track("Precon Documents Uploaded", { project_id: page.projectIdValue, kind, count: files.length })
      page._applyResponse(data)
      if (kind !== "owner_form") page._showToast(files.length === 1 ? this._t("plans.added_one") : this._t("plans.added_many", { count: files.length }))
    } catch (err) {
      chips.forEach(c => c.remove())
      page._showToast(err.message, "error")
    }
  }

  // A blue chip with a spinner while the file goes up; gray once it's there.
  _chip(box, name) {
    const chip = document.createElement("span")
    chip.className = "chip s1up"
    chip.innerHTML = `<svg class="animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden="true"><circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" opacity="0.25"></circle><path fill="currentColor" opacity="0.75" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path></svg><span></span>`
    chip.querySelector("span").textContent = name
    if (box) box.appendChild(chip)
    return chip
  }

  _chipDone(chip) {
    if (!chip) return
    chip.classList.remove("s1up")
    chip.querySelector("svg").outerHTML = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z"></path></svg>`
  }
}
